import { getLinkAttributes, unwrapLink, upsertLink } from '@platejs/link';
import { LinkPlugin } from '@platejs/link/react';
import { styled } from '@porsche-code/kado-styled-system/jsx';
import {
  PButton,
  PInputText,
  PLinkPure,
} from '@porsche-design-system/components-react';
import { type TLinkElement } from 'platejs';
import {
  useEditorRef,
  useEditorState,
  type PlateElementProps,
} from 'platejs/react';
import { useRef, useState, type MouseEvent } from 'react';

import LinkIcon from '../../../../../icons/link.svg?react';
import { Popover } from '../../../Popover';
import { ToolbarButton } from '../../ToolbarButton';
import { PopoverActionContainer, PopoverContent } from './shared';

const DEFAULT_LINK_URL = 'https://';
const ALLOWED_LINK_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'tel:']);
const DOMAIN_LIKE_URL =
  /^(localhost(?::\d+)?|(?:[\w-]+\.)+[\w-]{2,})(?:[/?#].*)?$/i;
const RELATIVE_LINK_PREFIXES = ['/', './', '../', '#', '?'];

const LinkElementWrapper = styled('span', {
  base: {
    display: 'inline',
  },
});

const sanitizeLinkUrl = (value: string) => {
  const trimmedValue = value.trim();

  if (!trimmedValue || trimmedValue.startsWith('//')) {
    return undefined;
  }

  if (
    RELATIVE_LINK_PREFIXES.some((prefix) => trimmedValue.startsWith(prefix))
  ) {
    return trimmedValue;
  }

  const hasProtocol = /^[a-zA-Z][a-zA-Z\d+.-]*:/.test(trimmedValue);

  if (!hasProtocol) {
    if (!DOMAIN_LIKE_URL.test(trimmedValue)) {
      return undefined;
    }

    try {
      return new URL(`https://${trimmedValue}`).toString();
    } catch {
      return undefined;
    }
  }

  try {
    const parsedUrl = new URL(trimmedValue);

    return ALLOWED_LINK_PROTOCOLS.has(parsedUrl.protocol)
      ? parsedUrl.toString()
      : undefined;
  } catch {
    return undefined;
  }
};

const LinkElement = ({
  attributes,
  children,
  element,
}: PlateElementProps & {
  element: TLinkElement;
}) => {
  const editor = useEditorRef();
  const linkAttributes = getLinkAttributes(editor, element);
  const sanitizedHref = sanitizeLinkUrl(linkAttributes.href ?? '');

  if (!sanitizedHref) {
    return <LinkElementWrapper {...attributes}>{children}</LinkElementWrapper>;
  }

  const target =
    sanitizedHref.startsWith('http://') || sanitizedHref.startsWith('https://')
      ? linkAttributes.target
      : undefined;

  return (
    <LinkElementWrapper {...attributes}>
      <PLinkPure icon="none" underline>
        <a
          href={sanitizedHref}
          rel={target === '_blank' ? 'noopener noreferrer' : undefined}
          target={target}
        >
          {children}
        </a>
      </PLinkPure>
    </LinkElementWrapper>
  );
};

export const linkPlugin = LinkPlugin.configure({
  render: {
    node: LinkElement,
  },
});

export const LinkToolbarButtons = ({
  insertLinkLabel = 'Insert link',
  applyLinkLabel = 'Apply link',
  removeLinkLabel = 'Remove link',
  sanitizeLinkInstruction = 'Enter a valid URL using https, http, mailto, tel, or a relative path.',
}: {
  insertLinkLabel?: string;
  applyLinkLabel?: string;
  removeLinkLabel?: string;
  sanitizeLinkInstruction?: string;
}) => {
  const editor = useEditorState();
  const [isOpen, setIsOpen] = useState(false);
  const [url, setUrl] = useState(DEFAULT_LINK_URL);
  const [urlError, setUrlError] = useState<string | undefined>();
  const [hasSavedLinkSelection, setHasSavedLinkSelection] = useState(false);
  const savedSelection = useRef<typeof editor.selection>(null);
  const isActive =
    !!editor.selection && editor.api.some({ match: { type: LinkPlugin.key } });
  const isLinkSelectionActive = isActive || hasSavedLinkSelection;

  const getSelectedLinkUrl = () => {
    const selection = savedSelection.current ?? editor.selection;

    if (!selection) {
      return undefined;
    }

    const linkEntry = editor.api.above({
      at: selection,
      match: { type: LinkPlugin.key },
    });

    if (!linkEntry) {
      return undefined;
    }

    const linkElement = linkEntry[0] as TLinkElement;

    return getLinkAttributes(editor, linkElement).href ?? linkElement.url;
  };

  const focusSavedSelection = () => {
    const target =
      savedSelection.current ?? editor.selection ?? editor.api.end([]);

    editor.tf.focus();
    editor.tf.select(target);
  };

  const resetPopover = () => {
    setUrl(DEFAULT_LINK_URL);
    setUrlError(undefined);
    setHasSavedLinkSelection(false);
    savedSelection.current = null;
    setIsOpen(false);
  };

  const applyLink = () => {
    const nextUrl = url.trim();

    if (nextUrl.length === 0) {
      focusSavedSelection();
      unwrapLink(editor);
      editor.tf.focus();
      resetPopover();
      return;
    }

    const sanitizedUrl = sanitizeLinkUrl(nextUrl);

    if (!sanitizedUrl) {
      setUrlError(sanitizeLinkInstruction);
      return;
    }

    focusSavedSelection();
    upsertLink(editor, {
      url: sanitizedUrl,
      target:
        sanitizedUrl.startsWith('http://') ||
        sanitizedUrl.startsWith('https://')
          ? '_blank'
          : undefined,
    });
    editor.tf.focus();
    resetPopover();
  };

  const removeLink = () => {
    focusSavedSelection();
    unwrapLink(editor);
    editor.tf.focus();
    resetPopover();
  };

  const handleOpenChange = (nextIsOpen: boolean) => {
    setIsOpen(nextIsOpen);

    if (nextIsOpen) {
      setUrl(getSelectedLinkUrl() ?? DEFAULT_LINK_URL);
      setUrlError(undefined);
      return;
    }

    setUrl(DEFAULT_LINK_URL);
    setUrlError(undefined);
    setHasSavedLinkSelection(false);
    savedSelection.current = null;
  };

  return (
    <Popover
      open={isOpen}
      onChange={handleOpenChange}
      target={
        <ToolbarButton
          active={isLinkSelectionActive || isOpen}
          tooltip={insertLinkLabel}
          type="button"
          onMouseDown={() => {
            savedSelection.current = editor.selection;
            setHasSavedLinkSelection(isActive);
          }}
          onClick={(event: MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            event.stopPropagation();
            handleOpenChange(!isOpen);
          }}
        >
          <LinkIcon />
        </ToolbarButton>
      }
    >
      <PopoverContent>
        <PInputText
          message={urlError}
          name="linkUrl"
          placeholder="https://"
          state={urlError ? 'error' : 'none'}
          value={url}
          onClick={(event: MouseEvent<HTMLElement>) => event.stopPropagation()}
          onInput={(event) => {
            setUrlError(undefined);
            setUrl((event.target as HTMLInputElement).value);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              applyLink();
            }
          }}
        />

        <PopoverActionContainer>
          <PButton type="button" onClick={applyLink}>
            {applyLinkLabel}
          </PButton>

          {isLinkSelectionActive && (
            <PButton type="button" variant="secondary" onClick={removeLink}>
              {removeLinkLabel}
            </PButton>
          )}
        </PopoverActionContainer>
      </PopoverContent>
    </Popover>
  );
};
